(() => {
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const stateByModal = new WeakMap();

  const getModalContent = (modal) => modal.querySelector('.product-media-modal__content');

  const getZoomTargets = (modal) => {
    const content = getModalContent(modal);
    if (!content) return [];
    return Array.from(content.querySelectorAll('img[data-media-id]')).filter((el) => el instanceof HTMLImageElement);
  };

  const resetImage = (img) => {
    img.style.removeProperty('transform');
    img.style.removeProperty('transform-origin');
    img.style.removeProperty('cursor');
    img.style.removeProperty('touch-action');
    img.classList.remove('jr-media-zoom--active');
  };

  const ensureModalState = (modal) => {
    let st = stateByModal.get(modal);
    if (st) return st;
    st = {
      activeImg: null,
      scale: 1,
      x: 0,
      y: 0,
      isDragging: false,
      dragStart: null,
      pointers: new Map(),
      pinchStart: null,
    };
    stateByModal.set(modal, st);
    return st;
  };

  const applyTransform = (modal) => {
    const st = ensureModalState(modal);
    const img = st.activeImg;
    if (!(img instanceof HTMLImageElement)) return;

    const content = getModalContent(modal);
    if (!(content instanceof HTMLElement)) return;

    const scale = clamp(st.scale, 1, 4);
    st.scale = scale;

    const imgRect = img.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();

    // Clamp translate so the image always covers the viewport when zoomed.
    const maxX = Math.max(0, (imgRect.width * scale - imgRect.width) / 2 + (contentRect.width - imgRect.width) / 2);
    const maxY = Math.max(0, (imgRect.height * scale - imgRect.height) / 2 + (contentRect.height - imgRect.height) / 2);

    st.x = clamp(st.x, -maxX, maxX);
    st.y = clamp(st.y, -maxY, maxY);

    img.style.transformOrigin = 'center center';
    img.style.transform = `translate3d(${st.x}px, ${st.y}px, 0) scale(${scale})`;

    if (scale > 1) {
      img.classList.add('jr-media-zoom--active');
      img.style.cursor = st.isDragging ? 'grabbing' : 'grab';
      img.style.touchAction = 'none';
    } else {
      resetImage(img);
    }
  };

  const setActiveImage = (modal, img) => {
    const st = ensureModalState(modal);
    if (st.activeImg && st.activeImg !== img) resetImage(st.activeImg);
    st.activeImg = img;
    st.scale = 1;
    st.x = 0;
    st.y = 0;
    st.isDragging = false;
    st.dragStart = null;
    st.pointers.clear();
    st.pinchStart = null;
    applyTransform(modal);
  };

  const onWheel = (modal, event) => {
    const st = ensureModalState(modal);
    if (!(st.activeImg instanceof HTMLImageElement)) return;

    event.preventDefault();
    const delta = event.deltaY;
    const next = st.scale + (delta > 0 ? -0.2 : 0.2);
    st.scale = clamp(next, 1, 4);
    applyTransform(modal);
  };

  const onPointerDown = (modal, event) => {
    const st = ensureModalState(modal);
    if (!(st.activeImg instanceof HTMLImageElement)) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    st.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (st.scale > 1) {
      st.isDragging = true;
      st.dragStart = { x: event.clientX, y: event.clientY, startX: st.x, startY: st.y };
    } else {
      st.isDragging = false;
      st.dragStart = null;
    }
    try {
      st.activeImg.setPointerCapture(event.pointerId);
    } catch {
      // ignore
    }
    applyTransform(modal);
  };

  const onPointerMove = (modal, event) => {
    const st = ensureModalState(modal);
    if (!(st.activeImg instanceof HTMLImageElement)) return;

    if (st.pointers.has(event.pointerId)) st.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    // Pinch zoom (2 pointers)
    if (st.pointers.size >= 2) {
      const pts = Array.from(st.pointers.values()).slice(0, 2);
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy);

      if (!st.pinchStart) {
        st.pinchStart = { dist, scale: st.scale };
      } else {
        const ratio = dist / Math.max(1, st.pinchStart.dist);
        st.scale = clamp(st.pinchStart.scale * ratio, 1, 4);
      }
      applyTransform(modal);
      return;
    }

    if (!st.isDragging || !st.dragStart) return;
    if (st.scale <= 1) return;
    st.x = st.dragStart.startX + (event.clientX - st.dragStart.x);
    st.y = st.dragStart.startY + (event.clientY - st.dragStart.y);
    applyTransform(modal);
  };

  const onPointerUp = (modal, event) => {
    const st = ensureModalState(modal);
    if (!(st.activeImg instanceof HTMLImageElement)) return;

    st.pointers.delete(event.pointerId);
    if (st.pointers.size < 2) st.pinchStart = null;
    if (st.pointers.size === 0) {
      st.isDragging = false;
      st.dragStart = null;
    }
    applyTransform(modal);
  };

  const attachToModal = (modal) => {
    if (!(modal instanceof HTMLElement)) return;
    if (modal.dataset.jrZoomInit === '1') return;
    modal.dataset.jrZoomInit = '1';

    const content = getModalContent(modal);
    if (!(content instanceof HTMLElement)) return;

    content.addEventListener('click', (event) => {
      const img = event.target instanceof HTMLElement ? event.target.closest('img[data-media-id]') : null;
      if (!(img instanceof HTMLImageElement)) return;
      if (ensureModalState(modal).activeImg !== img) setActiveImage(modal, img);
    });
    content.addEventListener(
      'wheel',
      (event) => {
        onWheel(modal, event);
      },
      { passive: false }
    );

    content.addEventListener('pointerdown', (event) => {
      const img = event.target instanceof HTMLElement ? event.target.closest('img[data-media-id]') : null;
      if (!(img instanceof HTMLImageElement)) return;
      if (ensureModalState(modal).activeImg !== img) setActiveImage(modal, img);
      onPointerDown(modal, event);
    });
    content.addEventListener('pointermove', (event) => onPointerMove(modal, event));
    content.addEventListener('pointerup', (event) => onPointerUp(modal, event));
    content.addEventListener('pointercancel', (event) => onPointerUp(modal, event));

    // Reset zoom whenever the modal closes.
    document.body.addEventListener('modalClosed', () => {
      const st = stateByModal.get(modal);
      if (!st) return;
      if (st.activeImg) resetImage(st.activeImg);
      stateByModal.delete(modal);
    });

    // Ensure an initial active image exists for wheel/pan behavior.
    const targets = getZoomTargets(modal);
    if (targets.length > 0) setActiveImage(modal, targets[0]);
  };

  const patchProductModal = () => {
    const ctor = customElements.get('product-modal');
    if (!ctor || !ctor.prototype) return;

    if (ctor.prototype.__jrZoomPatched) return;
    ctor.prototype.__jrZoomPatched = true;

    const originalShow = ctor.prototype.show;
    ctor.prototype.show = function (opener) {
      originalShow.call(this, opener);
      attachToModal(this);
    };
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      customElements.whenDefined('product-modal').then(patchProductModal).catch(() => {});
    });
  } else {
    customElements.whenDefined('product-modal').then(patchProductModal).catch(() => {});
  }
})();
